import {Component, ElementRef, inject, Input, OnInit, QueryList, signal, ViewChild, ViewChildren} from '@angular/core';
import {TeamMembership} from '../../../types/team-management.type';
import {TeamManagementService} from '../../../services/api/team-management.service';
import {AutoUnsubscribe} from '../../../decorators/auto-unsubscribe.decorator';
import {AuthService} from '../../../auth/shared/services/auth.service';
import {UserInfoQuery} from '../../../common/elf-store/user-info/user-info.query';
import {UserInfoState} from '../../../common/elf-store/user-info/user-info.type';
import {ToastrMessagesProvider} from '../../../services/toastr-messages.service';
import {ActivatedRouteSnapshot, NavigationExtras, Router, RouteReuseStrategy} from '@angular/router';
import {AuthQuery} from '../../../common/elf-store/auth/auth.query';
import {SUBSCRIPTIONS} from 'src/app/common/utils/constants';
import {MainLayoutComponent} from '../../smart/main-layout/main-layout.component';
import {UserPermissionsService} from 'src/app/services/user-permissions.service';
import {PaymentDataService} from 'src/app/services/payment-data.service';
import {AccountService} from 'src/app/services/account.service';
import {filter} from 'rxjs';
import {AgencyService} from 'src/app/services/agency.service';
import {TeamSelectorService} from './team-selector.service';
import {TeamQuery} from '../../../common/elf-store/team/team.query';
// [NEW-GROUP] - disabled for ticket DASH-1168
// import {FeatureFlagQuery} from '../../../common/elf-store/feature-flag/feature-flag.query';
import {UserPermissions} from '../../../common/elf-store/user-permissions/user-permissions.store';
import {
  ExtendedJWTUserDetailsDto,
  TargetAccountRecord,
  TeamMemberDtoMemberAccessTypeEnum,
} from '../../../../userway/sdk/SSOApiClient';
import {ErrorMessage} from '../../../types/toastr-notifications.type';
import {ToastrNotificationsService} from '../../../services/toastr-notifications.service';
import {ApiClient} from '../../../services/api/userway-api.service';
import {EventEmitterService} from '../../../services/event-emitter.service';
import {TooltipPanelWrapperComponent} from '../tooltip-panel-wrapper/tooltip-panel-wrapper.component';
import {TeamNavLinkComponent} from '../../smart/main-layout/team-nav-link/team-nav-link.component';
import {TooltipPanelComponent} from '../tooltip-panel/tooltip-panel.component';
import {ClickOutsideDirective} from '../../../common/directives/click-outside.directive';
import {TeamItemComponent} from './components/team-item/team-item.component';
import {PermissionGuardDirective} from '../../../common/directives/permission-guard.directive';

@Component({
  standalone: true,
  selector: 'mc-team-selector',
  templateUrl: './team-selector.component.html',
  styleUrls: ['./team-selector.component.scss'],
  imports: [
    TooltipPanelWrapperComponent,
    TeamNavLinkComponent,
    TooltipPanelComponent,
    ClickOutsideDirective,
    TeamItemComponent,
    PermissionGuardDirective,
  ],
})
@AutoUnsubscribe()
export class TeamSelectorComponent implements OnInit {
  teamManagementService = inject(TeamManagementService);
  authService = inject(AuthService);
  private authQuery = inject(AuthQuery);
  private userInfoQuery = inject(UserInfoQuery);
  router = inject(Router);
  private toastr = inject(ToastrMessagesProvider);
  private userPermissionsService = inject(UserPermissionsService);
  private paymentDataService = inject(PaymentDataService);
  private accountService = inject(AccountService);
  private agencyService = inject(AgencyService);
  private teamSelectorService = inject(TeamSelectorService);
  private routeReuseStrategy = inject(RouteReuseStrategy);
  private teamQuery = inject(TeamQuery);
  private apiClient = inject(ApiClient);

  @Input() isSidenavOpened: boolean;

  @ViewChild(TeamNavLinkComponent) private navLink?: TeamNavLinkComponent;
  @ViewChildren(TeamItemComponent) private teamItems?: QueryList<TeamItemComponent>;

  openedTeamList = false;
  token: string;
  refreshToken: string;
  regulationTrackerMode: boolean = false;
  userMemberships: TeamMembership[];

  public userId: string;
  private targetUserId: string;

  // [NEW-GROUP] - disabled for ticket DASH-1168
  // groupsFeatureFlag: boolean = false;

  isLoading = signal<boolean>(false);

  toastNotificationService = inject(ToastrNotificationsService);
  eventEmitterService = inject(EventEmitterService);
  elementRef = inject(ElementRef);

  get UserPermissions() {
    return UserPermissions;
  }

  isOwner(member: TeamMembership) {
    return member.memberAccessType === TeamMemberDtoMemberAccessTypeEnum.ORIGINAlOWNER;
  }

  loadActiveMembership() {
    return this.teamManagementService.loadActiveMembership().subscribe({
      next: () => {
        this.handleOwnerIdParams();
        this.isLoading.set(false);
      },
    });
  }

  private handleOwnerIdParams(): void {
    const ownerId = this.teamManagementService.siteDetailsUrlParams().ownerId;
    if (!ownerId) return;

    const owner = this.userMemberships.find(user => user.myMembershipTeamOwnerAccountId === ownerId);

    if (!owner) {
      this.toastNotificationService.notifyError({error: null, message: ErrorMessage.TeamAccountNotFound});
      this.teamManagementService.resetSiteDetailsUrlParams();
      return;
    }

    if ((ownerId && this.isOwner(owner)) || this.authService.uWTargetSwitchId === ownerId) {
      return;
    }

    this.switchToTeam(owner);
  }

  ngOnInit() {
    // [NEW-GROUP] - disabled for ticket DASH-1168
    // this[SUBSCRIPTIONS].add(
    //   this.featureFlagQuery.groupsFeature$.subscribe(groupsFeatureFlag => {
    //     this.groupsFeatureFlag = groupsFeatureFlag;
    //     this.loadActiveMembership();
    //   }),
    // );
    this.loadActiveMembership();

    this[SUBSCRIPTIONS].add(
      this.teamQuery.userMemberships$.subscribe({
        next: userMemberships => {
          this.userMemberships = userMemberships;
        },
      }),
    );

    this[SUBSCRIPTIONS].add(
      this.userInfoQuery.userInfo$
        .pipe(filter(userInfo => !!userInfo.ssoUserId || !!userInfo.id))
        .subscribe((info: UserInfoState) => {
          this.userId = info.ssoUserId;
          this.targetUserId = info.targetSsoUserId;
          this.regulationTrackerMode = info.regulationTrackerMode;
        }),
    );

    this[SUBSCRIPTIONS].add(
      this.teamSelectorService.changeTeam$.subscribe((teamMembership: TeamMembership) => {
        this.switchToTeam(teamMembership);
      }),
    );
    this[SUBSCRIPTIONS].add(this.authQuery.token$.subscribe(token => (this.token = token)));
    this[SUBSCRIPTIONS].add(this.authQuery.refreshToken$.subscribe(refreshToken => (this.refreshToken = refreshToken)));
  }

  onToggleMenu(): void {
    if (this.openedTeamList) {
      this.closeAndFocusButton();
    } else {
      this.openMenu();
    }
  }

  focusMenuItem(): void {
    if (!this.openedTeamList) return;
    const items = (this.teamItems?.toArray() ?? []).filter(item => !!item.element);
    items[0]?.focus();
  }

  private openMenu(): void {
    if (!this.userMemberships?.length) return;
    this.openedTeamList = true;
  }

  closeAndFocusButton(): void {
    this.closeTeamList();
    this.navLink?.focusMenuButton();
  }

  handleMenuKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.closeAndFocusButton();
      return;
    }

    const items = (this.teamItems?.toArray() ?? []).filter(item => !!item.element);
    if (!items.length) return;

    const currentIndex = items.findIndex(item => item.element === document.activeElement);
    let nextIndex: number;

    switch (event.key) {
      case 'ArrowDown':
      case 'ArrowRight':
        nextIndex = Math.min(currentIndex + 1, items.length - 1);
        break;
      case 'ArrowUp':
      case 'ArrowLeft':
        nextIndex = Math.max(currentIndex - 1, 0);
        break;
      case 'Home':
        nextIndex = 0;
        break;
      case 'End':
        nextIndex = items.length - 1;
        break;
      default:
        return;
    }

    event.preventDefault();
    items[nextIndex]?.focus();
  }

  handleFocusOut(event: FocusEvent): void {
    if (event?.relatedTarget && !this.elementRef.nativeElement.contains(event.relatedTarget)) {
      this.closeTeamList();
    }
  }

  openTeamList() {
    if (this.userMemberships) {
      this.openedTeamList = true;
    }
  }

  closeTeamList(event?: Event): void {
    const target = event?.target as HTMLElement;
    // Clicks on the nav-link itself are handled by its own toggle logic, so ignore them here.
    if (target?.closest('mc-team-nav-link')) {
      return;
    }
    this.openedTeamList = false;
  }

  async switchToTeam(team: TeamMembership): Promise<void> {
    this.closeTeamList();
    this.isLoading.set(true);

    if (this.isSwitchedTeamIsOwner(team)) {
      // When we switch to owners team - default team on login
      this.handleUserInfoUpdate(team);
    } else if (await this.isNewSwitchedTeam(team)) {
      // When we switch to another team
      this.handleTeamInfoUpdate(team);
    } else {
      // When we switch on current active team
      this.isLoading.set(false);

      const accountId = team.myMembershipTeamOwnerInfo.accountId;
      // [NEW-GROUP] - disabled for ticket DASH-1168
      // const pathToRedirect = this.groupsFeatureFlag ? `/account-management/account/${accountId}` : `/team`;
      const pathToRedirect = `/team`;

      this.router.navigate([pathToRedirect]);
    }
  }

  private isSwitchedTeamIsOwner(team: TeamMembership): boolean {
    return (
      (this.authService.uWTargetSwitchId && team.myMembershipTeamOwnerAccountId === this.userId) ||
      (this.authService.isSSOLogin && this.targetUserId === team.myMembershipTeamOwnerAccountId)
    );
  }

  private handleUserInfoUpdate(team: TeamMembership): void {
    this.authService.setTargetTeamId(team.myMembershipTeamOwnerAccountId);
    this.eventEmitterService.updateHeaders.next(true);
    this.accountService.getTeamOwnerInfo(team.myMembershipTeamOwnerInfo.accountCode).subscribe({
      next: () => {
        this.resetUserOnSwitch();
        this.updateDashboard(team);
      },
      error: () => {
        this.authService.clearTargetId();
        this.isLoading.set(false);
      },
    });
  }

  private isNewSwitchedTeam(team: TeamMembership): Promise<boolean> {
    return team.myMembershipTeamOwnerAccountId !== this.userId && this.isMfaSetupNeeded(team);
  }

  async isMfaSetupNeeded(team: TeamMembership): Promise<boolean> {
    const isUserNotHaveMfa = !this.authService.isMfa;
    const accountIfo = this.accountService.getInfoFromJWT();
    let targetAccount = null;
    if (accountIfo.hasExtendedToken && !accountIfo.targetAccounts) {
      const targetAccounts: ExtendedJWTUserDetailsDto = await this.accountService.getExtendedTokenData();
      targetAccount = this.getTargetAccount(targetAccounts.targetAccounts, team.myMembershipTeamOwnerAccountId);
    } else {
      targetAccount = this.getTargetAccount(accountIfo.targetAccounts, team.myMembershipTeamOwnerAccountId);
    }

    if (targetAccount?.requiredMfa && isUserNotHaveMfa) {
      this.isLoading.set(false);
      this.toastr
        .warning('To continue working with the team, please&nbsp;<span class="toast-link">set up 2FA</span>', true)
        .onTap.subscribe(() => this.router.navigate(['settings/general']));
      return false;
    }
    return true;
  }

  private getTargetAccount(
    targetAccounts: TargetAccountRecord[],
    myMembershipTeamOwnerAccountId: string,
  ): TargetAccountRecord {
    return targetAccounts?.find(account => account.targetSsoUserId === myMembershipTeamOwnerAccountId);
  }

  private handleTeamInfoUpdate(team: TeamMembership): void {
    this.authService.setTargetTeamId(team.myMembershipTeamOwnerAccountId);
    this.eventEmitterService.updateHeaders.next(true);
    this.accountService.getTeamOwnerInfo(team.myMembershipTeamOwnerInfo.accountCode).subscribe({
      next: () => {
        this.resetUserOnSwitch();
        this.updateDashboard(team);
      },
      error: () => {
        this.authService.clearTargetId();
        this.isLoading.set(false);
      },
    });
  }

  private resetUserOnSwitch() {
    this.agencyService.clearAgencyData();
    this.paymentDataService.resetCustomerAndPaymentData();
    this.userPermissionsService.resetPermissions();
    this.accountService.clearUserInfo();
    this.paymentDataService.resetPaymentProperties();
  }

  private updateDashboard(team: TeamMembership): void {
    const ownerId = team.myMembershipTeamOwnerAccountId;

    this.userPermissionsService.loadUserPermissions(ownerId).subscribe({
      next: () => {
        const previousReuseStrategy = this.routeReuseStrategy.shouldReuseRoute;
        this.routeReuseStrategy.shouldReuseRoute = (_future, _curr) => false;

        this.teamSelectorService.teamChanged();

        const targetRoute = this.getTargetRoute();
        const navigationExtras = this.getQueryParamsIfNeeded();

        this.router.navigate(targetRoute, navigationExtras).then(() => {
          this.routeReuseStrategy.shouldReuseRoute = previousReuseStrategy;
          this.teamManagementService.resetSiteDetailsUrlParams();

          this.isLoading.set(false);
          this.toastr.success(`Success switch to team - ${team.myMembershipTeamName}`);
        });
      },
    });
  }

  private getTargetRoute(): any[] {
    return this.teamManagementService.siteDetailsUrlParams().ownerId
      ? ['widget/my-sites/p/1/site-details/report']
      : [this.regulationTrackerMode ? '/regulation' : '/'];
  }

  private getQueryParamsIfNeeded(): NavigationExtras | undefined {
    if (this.teamManagementService.siteDetailsUrlParams().ownerId) {
      return {
        queryParams: {site: this.teamManagementService.siteDetailsUrlParams().siteName},
        queryParamsHandling: 'merge',
      };
    }
    return undefined;
  }
}
